import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormControl } from "@angular/forms";

import {BehaviorSubject, Observable, Subscription} from "rxjs";

import { Pending } from "../../../shared-library/decorators/pending.decorator";
import { PaginationOptionsModel } from "../../../shared-library/form-controls/pagination/pagination-options.model";
import { DisposeMixin, EmptyClassInstance } from "../../../shared-library/mixins/dispose.mixin";
import { LockMixin } from "../../../shared-library/mixins/lock.mixin";
import { LatLngModel } from "../../../shared-library/models/geolocation/lat-lng.model";
import { BrowserWindowService } from "../../../shared-library/services/environment/browser-window.service";
import { MapQuestGeolocationService } from "../../../shared-library/services/geolocation/map-quest-geolocation.service";

import { BaseSearchTabConfigModel } from "../../models/base-search-tab-config.model";
import { PaginationOptionsFactoryService } from "../../services/pagination-options-factory.service";
import { GoogleMapComponent } from "./components/google-map/google-map.component";
import { DirectorySearchForm } from "./forms/directory-search.form";
import { DirectoryItemModel } from "./models/directory-item.model";
import { SearchParametersModel } from "./models/search-parameters.model";
import { SearchResultsModel } from "./models/search-results.model";
import { DirectorySearchParametersFactoryService } from "./services/directory-search-parameters-factory.service";
import { DirectorySearchService } from "./services/directory-search.service";
import { DirectorySearchTabConfigModel } from "./models/directory-search-tab-config.model";
import { SearchFiltersModel } from "./models/search-filters.model";
import { SearchTabAlias } from "../../enums/search-tab-alias.enum";
import moment from "moment";

@Component({
    selector: "pcg-ng-directory-search",
    template: require('./directory-search.component.html')
})
export class DirectorySearchComponent extends DisposeMixin(LockMixin(EmptyClassInstance)) implements OnInit, AfterViewInit, OnDestroy {
    @Input()
    public searchTabConfig: DirectorySearchTabConfigModel;

    @Input()
    public onTabActivated: Observable<BaseSearchTabConfigModel>;

    @ViewChild("mapContainer", { static: false })
    public mapContainerRef: GoogleMapComponent;

    public searchForm: DirectorySearchForm;
    public searchResult: SearchResultsModel;

    public postcodeControl: FormControl;
    public distanceControl: FormControl;
    public datesSpan$: BehaviorSubject<{startDate: string, endDate: string}>;

    public isMapInitialized: boolean = false;
    public isMapUpdated: boolean = false;
    public isMapVisible: boolean = true;

    public paginationOptions: PaginationOptionsModel;
    public tabAlias = SearchTabAlias;

    @Pending
    private _searchInProgressSubscription: Subscription;

    private _subscriptions: Subscription = new Subscription();

    public constructor(
        private _browserWindowService: BrowserWindowService,
        private _geolocationService: MapQuestGeolocationService,
        private _paginationOptionsFactory: PaginationOptionsFactoryService,
        private _searchParametersFactory: DirectorySearchParametersFactoryService,
        private _searchService: DirectorySearchService
    ) {
        super();
    }

    public ngOnInit(): void {
        this.paginationOptions = this._paginationOptionsFactory.createDefaultPaginationOptions();

        const initialSearchParams = this._searchParametersFactory.createFromQueryString(this._browserWindowService.getQueryString(), this.searchTabConfig);
        const emptySearchParams = this._searchParametersFactory.createFromQueryString("", this.searchTabConfig);

        this.searchForm = DirectorySearchForm.createForm(initialSearchParams, emptySearchParams);

        this.postcodeControl = new FormControl(this.searchForm.postcode.value);
        this.distanceControl = new FormControl(this.searchForm.distance.value);
        this.datesSpan$ = new BehaviorSubject<{startDate: string, endDate: string}>({
            startDate: initialSearchParams.startDate, endDate: initialSearchParams.endDate}
        );

        this._subscriptions
            .add(this.searchForm.onFormChanged.subscribe(
                (searchParams: SearchParametersModel) => {
                    return this.invokeSearch(searchParams);
                }
            ))
            .add(this.searchForm.onFormSubmitted.subscribe(
                (searchParams: SearchParametersModel) => {
                    return this.invokeSearch(searchParams);
                }
            ))
            .add(this.onTabActivated.subscribe(
                (tab) => this.onTabActivatedHandler(tab)
            ))
            .add(this.datesSpan$.subscribe(
                (dateSpan) => {
                    this.searchForm.startDate.setValue(dateSpan.startDate);
                    this.searchForm.endDate.setValue(dateSpan.endDate);
                }
            ));
    }

    public ngAfterViewInit(): void {
        this.mapContainerRef.initializeMap();
        this.searchForm.submit();
    }

    public ngOnDestroy(): void {
        this.dispose();
        this.datesSpan$.complete();
        this.searchForm.dispose();
    }

    public onSearchButtonClicked(): void {
        if (this.postcodeControl.value && this.distanceControl.value) {
            this._geolocationService.getCoordinatesByAddress(this.postcodeControl.value).subscribe(
                (location: LatLngModel) => {
                    this.mapContainerRef.setMapCenter(location.lat, location.lng);
                },
                () => {
                }
            );

            this.searchForm.pageNumber.setValue(1, { emitEvent: false });
            this.searchForm.postcode.setValue(this.postcodeControl.value, { emitEvent: false });
            this.searchForm.distance.setValue(this.distanceControl.value, { emitEvent: false });
            if (this.searchForm.sortOption.untouched) {
                this.searchForm.sortOption.setValue("Distance;asc", { emitEvent: false });
            }

            this.searchForm.submit();
        }
    }

    private onTabActivatedHandler(activatedTab: BaseSearchTabConfigModel): void {
        if (activatedTab.tabAlias === this.searchTabConfig.tabAlias) {
            this._browserWindowService.setQueryString(this._searchParametersFactory.serialiseToQueryString(this.searchForm.formValue));
        }
    }

    private onToggleMapButtonClicked(): void {
        if (!this.isMapInitialized) {
            this.mapContainerRef.initializeMap();
            this.isMapInitialized = true;
        }

        setTimeout(() => {
            this.isMapVisible = !this.isMapVisible;

            if (this.isMapVisible && !this.isMapUpdated) {
                this.mapContainerRef.updateMapMarkers(this.searchResult.pageItems);
                this.isMapUpdated = true;
            }
        });
    }

    private invokeSearch(searchParams: SearchParametersModel): void {
        searchParams.filters = new SearchFiltersModel();
        searchParams.filters.categories = searchParams.categories;
        searchParams.filters.eventCategories = searchParams.eventCategories;
        searchParams.filters.needsOfChildren = searchParams.needsOfChildren;
        searchParams.filters.postcodes = searchParams.postcodes;
        searchParams.filters.age = searchParams.age;
        searchParams.filters.frequency = searchParams.frequency;
        searchParams.filters.dietaryNeeds = searchParams.dietaryNeeds;
        searchParams.filters.accessibility = searchParams.accessibility;
        searchParams.filters.provisionTypes = searchParams.provisionTypes;
        searchParams.filters.schoolNeeds = searchParams.schoolNeeds;
        searchParams.filters.specialDietCater = searchParams.specialDietCater;
        searchParams.filters.postcode = searchParams.postcode;
        searchParams.filters.distance = searchParams.distance;
        searchParams.filters.startDate = searchParams.startDate;
        searchParams.filters.endDate = searchParams.endDate;
        searchParams.startDate = null;
        searchParams.endDate = null;

        if (this.searchTabConfig.isActive) {
            this._browserWindowService.setQueryString(this._searchParametersFactory.serialiseToQueryString(searchParams));
        }

        this._searchInProgressSubscription = this._searchService.getSearchResults(searchParams).subscribe(
            (result: SearchResultsModel) => {
                if (result && result.pageItems) {
                    result.pageItems = result.pageItems.map(model => new DirectoryItemModel(model));
                }

                // if (this.searchTabConfig.tabAlias === this.tabAlias.EVENTS_SEARCH && searchParams.sortOption === "StartDate;asc") {
                //     result.pageItems.sort((a: DirectoryItemModel, b: DirectoryItemModel) => {
                //
                //         const todayMidnight = moment.utc().startOf("day");
                //
                //         const datesA = a.occurrences
                //             .map(date => moment(date))
                //             .filter(m => m.isValid())
                //             .filter(m => m.isAfter(todayMidnight))
                //             .slice(0, 11);
                //         const datesB = b.occurrences
                //             .map(date => moment(date))
                //             .filter(m => m.isValid())
                //             .filter(m => m.isAfter(todayMidnight))
                //             .slice(0, 11);
                //
                //         return datesA.splice(0,1)[0]?.valueOf() - datesB.splice(0,1)[0]?.valueOf();
                //     });
                // }

                this.searchResult = result;

                window.scrollTo(0, 0);

                this.mapContainerRef.updateMapMarkers(this.searchResult.pageItems);
            },
            () => {
            }
        );
    }
}
